import { supabase } from "@/integrations/supabase/client";
import type { Enums, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

// ── User roles ──

export function fetchUserRole(userId: string) {
  return supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .single();
}

export function fetchUserRoleMaybe(userId: string) {
  return supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .maybeSingle();
}

export function fetchUserRoles(userId: string) {
  return supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
}

export function insertUserRole(userId: string, role: Enums<"app_role">) {
  const row: TablesInsert<"user_roles"> = { user_id: userId, role };
  return supabase.from("user_roles").insert(row);
}

export function updateUserRole(userId: string, role: Enums<"app_role">) {
  const patch: TablesUpdate<"user_roles"> = { role };
  return supabase.from("user_roles").update(patch).eq("user_id", userId);
}

export function deleteUserRole(userId: string) {
  return supabase
    .from("user_roles")
    .delete()
    .eq("user_id", userId);
}

// ── Profiles ──

export function fetchProfile(userId: string) {
  return supabase
    .from("profiles")
    .select("first_name, last_name")
    .eq("user_id", userId)
    .single();
}

export function fetchProfileMaybe(userId: string) {
  return supabase
    .from("profiles")
    .select("first_name, last_name")
    .eq("user_id", userId)
    .maybeSingle();
}

export function fetchProfileWizardStatus(userId: string) {
  return supabase
    .from("profiles")
    .select("wizard_completed_at")
    .eq("user_id", userId)
    .maybeSingle();
}

export function fetchProfileFull(userId: string) {
  return supabase
    .from("profiles")
    .select("*")
    .eq("user_id", userId)
    .single();
}

export function updateProfile(userId: string, data: TablesUpdate<"profiles">) {
  return supabase
    .from("profiles")
    .update(data)
    .eq("user_id", userId);
}

export function fetchProfileOnboardingFlags(userId: string) {
  return supabase
    .from("profiles")
    .select("onboarding_dismissed, wizard_completed_at")
    .eq("user_id", userId)
    .single();
}

// ── Onboarding progress ──

export function fetchOnboardingProgress(userId: string) {
  return supabase
    .from("onboarding_progress")
    .select("step_key")
    .eq("user_id", userId);
}

export function upsertOnboardingStep(userId: string, stepKey: string) {
  return supabase
    .from("onboarding_progress")
    .upsert(
      { user_id: userId, step_key: stepKey },
      { onConflict: "user_id,step_key" },
    );
}

export function deleteOnboardingProgress(userId: string) {
  return supabase
    .from("onboarding_progress")
    .delete()
    .eq("user_id", userId);
}
